import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './BlockedAccountsScreen.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  return (
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('barterfiToken') ||
    localStorage.getItem('userToken') ||
    ''
  );
};

const getAuthHeaders = () => {
  const token = getToken();

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '/profile/placeholder-profile.png';

  if (profilePic.startsWith('http')) return profilePic;

  return profilePic;
};

const BlockedAccountsScreen = () => {
  const navigate = useNavigate();

  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblockingUsername, setUnblockingUsername] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const fetchBlockedUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await fetch(`${API_BASE}/api/privacy/blocked`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch blocked accounts.');
      }

      setBlockedUsers(Array.isArray(data.blockedUsers) ? data.blockedUsers : []);
    } catch (err) {
      console.error('❌ fetchBlockedUsers error:', err);
      setError(err.message || 'Failed to fetch blocked accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const showToast = (message) => {
    setToast(message);

    setTimeout(() => {
      setToast('');
    }, 2200);
  };

  const handleUnblock = async (targetUsername) => {
    if (!targetUsername || unblockingUsername) return;

    try {
      setUnblockingUsername(targetUsername);
      setError('');

      const res = await fetch(`${API_BASE}/api/privacy/unblock`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ targetUsername }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to unblock user.');
      }

      setBlockedUsers((prev) =>
        prev.filter(
          (user) =>
            String(user.username).toLowerCase() !==
            String(targetUsername).toLowerCase()
        )
      );

      showToast(data.message || `@${targetUsername} has been unblocked.`);
    } catch (err) {
      console.error('❌ unblock user error:', err);
      setError(err.message || 'Failed to unblock user.');
    } finally {
      setUnblockingUsername('');
    }
  };

  return (
    <div className="blocked-accounts-screen">
      <div className="blocked-accounts-topbar">
        <button
          className="blocked-accounts-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ‹
        </button>

        <h2 className="blocked-accounts-title">Blocked Accounts</h2>

        <div className="blocked-accounts-topbar-spacer" />
      </div>

      <div className="blocked-accounts-content">
        <p className="blocked-accounts-info">
          People you block will appear here. You can unblock them anytime.
        </p>

        {loading && (
          <div className="blocked-accounts-state">
            <div className="blocked-accounts-loader" />
            <p>Loading blocked accounts...</p>
          </div>
        )}

        {!loading && error && (
          <div className="blocked-accounts-error">
            <p>{error}</p>

            <button
              className="blocked-accounts-retry-btn"
              onClick={fetchBlockedUsers}
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && blockedUsers.length === 0 && (
          <div className="blocked-accounts-empty">
            <div className="blocked-accounts-empty-icon">🚫</div>
            <h3>No blocked accounts</h3>
            <p>When you block someone, they will show here.</p>
          </div>
        )}

        {!loading && !error && blockedUsers.length > 0 && (
          <div className="blocked-accounts-list">
            {blockedUsers.map((user) => {
              const isUnblocking = unblockingUsername === user.username;

              return (
                <div className="blocked-account-row" key={user._id || user.username}>
                  <div className="blocked-account-left">
                    <img
                      src={getProfilePicUrl(user.profilePic)}
                      alt={user.username}
                      className="blocked-account-avatar"
                      onError={(e) => {
                        e.currentTarget.src = '/profile/placeholder-profile.png';
                      }}
                    />

                    <div className="blocked-account-text">
                      <div className="blocked-account-name">
                        {user.name || user.username}
                      </div>

                      <div className="blocked-account-username">
                        @{user.username}
                      </div>
                    </div>
                  </div>

                  <button
                    className="blocked-account-unblock-btn"
                    disabled={isUnblocking}
                    onClick={() => handleUnblock(user.username)}
                  >
                    {isUnblocking ? 'Unblocking...' : 'Unblock'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && <div className="blocked-accounts-toast">{toast}</div>}
    </div>
  );
};

export default BlockedAccountsScreen;
